import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { useTranslation } from 'react-i18next'

import { Badge, HStack, Text } from '@enechain/ecloud-designsystem'
import {
  FaChevronDownRegular,
  FaChevronRightRegular,
} from '@enechain/ecloud-icons'

import { IconOnlyButton } from '~/components/common/IconOnlyButton'
import {
  AuditCell,
  DeleteIconButton,
  EditIconButton,
  MasterDataTable,
  type MasterDataTableColumn,
  type MasterDataTableGroupCell,
} from '~/components/common/MasterDataTable'
import { sortableColumn } from '~/components/common/SortableColumnHeader'
import { iconSize } from '~/constants/iconSize'
import { useMemberDisplayName } from '~/hooks/member/useMemberDisplayName'
import { convertAreaLabel } from '~/models/area'
import type { CivilDate } from '~/models/civilDate'
import type { SortOrder } from '~/models/tableSort'
import { isActiveOn } from '~/models/validPeriod'
import {
  affiliationCellOf,
  formatCivilSlash,
} from '~/pages/AdminPlantUnitMaster/models/affiliation'
import { generatorCategoryLabel } from '~/pages/AdminPlantUnitMaster/models/labels'
import type { GenerationSiteRow } from '~/pages/AdminPlantUnitMaster/models/siteRows'
import {
  type GroupedUnitRow,
  type MixedValue,
  type PlantUnitRow,
  type UnitSort,
  type UnitSortKey,
  nextUnitSort,
} from '~/pages/AdminPlantUnitMaster/models/unitRows'

const NO_VALUE = '—'

/** 一覧が系統コードの表示に使う広域機関の発電所の最小の形。 */
export type PlantCodeSource = {
  plantCode: string
  validFrom: CivilDate
  validTo: CivilDate | undefined
}

type Props = {
  rows: GroupedUnitRow[]
  isLoading: boolean
  today: CivilDate
  /** plant_master id → 系統コード・有効期間。 */
  plants: ReadonlyMap<string, PlantCodeSource>
  /** generation_site id → 発電所のエリア (エリア不一致の印に使う)。 */
  siteAreas: ReadonlyMap<string, AreaCode>
  siteAudits?: ReadonlyMap<
    string,
    Pick<GenerationSiteRow, 'updatedAt' | 'updatedBy'>
  >
  /** 今の並び (列見出しを押すまでは undefined = 既定順)。 */
  sort: UnitSort | undefined
  onSortChange: (sort: UnitSort) => void
  onToggleGroup: (siteId: string) => void
  onOpenDetail: (unit: PlantUnitRow) => void
  onDelete: (unit: PlantUnitRow) => void
  /** マスタ未設定の印を出すか (注意を出している間は印も出す)。 */
  showMissingBadges: boolean
  emptyLabel: string
  errorLabel?: string
}

/**
 * 電源ユニットの一覧表: 発電所 (サイト) ごとの見出し行 (開閉できる) + 配下のユニット行。
 * ユニット行のクリックと行末の詳細で詳細ページへ移る。
 */
export const PlantUnitTable = ({
  rows,
  isLoading,
  today,
  plants,
  siteAreas,
  siteAudits,
  sort,
  onSortChange,
  onToggleGroup,
  onOpenDetail,
  onDelete,
  showMissingBadges,
  emptyLabel,
  errorLabel,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const resolveDisplayName = useMemberDisplayName()
  const f = (key: string): string =>
    t(`page:adminPlantUnitMaster.unit.field.${key}`)

  const unitCell =
    (render: (unit: PlantUnitRow) => React.ReactNode) =>
    (row: GroupedUnitRow): React.ReactNode =>
      row.kind === 'unit' ? render(row.unit) : null

  const orderOf = (key: UnitSortKey): SortOrder | undefined =>
    sort?.key === key ? sort.order : undefined
  const sortableOf = (
    key: UnitSortKey,
    label: string,
  ): ReturnType<typeof sortableColumn> =>
    sortableColumn({
      order: orderOf(key),
      label,
      nextOrder: nextUnitSort(sort, key).order,
      onClick: () => onSortChange(nextUnitSort(sort, key)),
    })

  const missingBadge = (hint: string): React.ReactNode =>
    showMissingBadges ? (
      <Badge whiteSpace="nowrap" status="warning" title={hint}>
        {t('page:adminPlantUnitMaster.unit.missing.badge')}
      </Badge>
    ) : (
      NO_VALUE
    )

  const plantCodeCell = (unit: PlantUnitRow): React.ReactNode => {
    if (unit.plantMasterId === undefined) {
      return missingBadge(
        t('page:adminPlantUnitMaster.unit.missing.plantCodeHint'),
      )
    }
    const plant = plants.get(unit.plantMasterId)
    if (plant === undefined) return NO_VALUE
    if (isActiveOn(plant.validFrom, plant.validTo, today))
      return plant.plantCode
    // 広域機関の発電所の有効期間外でも、コードは出したまま低強調 (登録漏れを見るため)
    return (
      <Text
        as="span"
        color="Text.lowEmphasis"
        title={f('plantCodeOutOfPeriod')}
      >
        {plant.plantCode}
      </Text>
    )
  }

  // エリア列: 系統コードのエリア (無ければ発電所のエリア)。発電所と系統コードのエリアが違えば
  // 同じセルの中に印を添える (行は増やさない。中身は hover)
  const areaCell = (unit: PlantUnitRow): React.ReactNode => {
    const plant =
      unit.plantMasterId === undefined
        ? undefined
        : plants.get(unit.plantMasterId)
    const siteArea = siteAreas.get(unit.generationSiteId)
    if (plant === undefined || !unit.siteAreaMismatch) {
      return convertAreaLabel(unit.areaCode)
    }
    return (
      <HStack gap={1}>
        <Text as="span" whiteSpace="nowrap">
          {convertAreaLabel(unit.areaCode)}
        </Text>
        <Badge
          whiteSpace="nowrap"
          status="warning"
          title={t('page:adminPlantUnitMaster.unit.missing.areaMismatchHint', {
            siteArea: convertAreaLabel(siteArea ?? unit.areaCode),
            plantCode: plant.plantCode,
            plantArea: convertAreaLabel(unit.areaCode),
          })}
        >
          {t('page:adminPlantUnitMaster.unit.missing.areaMismatch')}
        </Badge>
      </HStack>
    )
  }

  const affiliationCell = (unit: PlantUnitRow): React.ReactNode => {
    const cell = affiliationCellOf(
      unit.plantMasterId !== undefined,
      unit.affiliations,
      today,
    )
    switch (cell.kind) {
      case 'noPlant':
        return NO_VALUE
      case 'unset':
        return missingBadge(t('page:adminPlantUnitMaster.unit.missing.bgHint'))
      case 'memberships':
        return (
          <Text
            as="span"
            color={cell.outOfPeriod ? 'Text.lowEmphasis' : undefined}
            title={
              cell.outOfPeriod
                ? t('page:adminPlantUnitMaster.unit.field.bgOutOfPeriod', {
                    periods: cell.periods.join(' / '),
                  })
                : cell.periods.join(' / ')
            }
          >
            {cell.names.join(' / ')}
            {cell.notManaged && (
              <Text
                as="span"
                marginLeft={1}
                color="Text.lowEmphasis"
                title={f('notManagedHint')}
              >
                {f('notManaged')}
              </Text>
            )}
          </Text>
        )
    }
  }

  const dateCell = (d: CivilDate | undefined): string =>
    d === undefined ? NO_VALUE : formatCivilSlash(d)

  const columns: Array<MasterDataTableColumn<GroupedUnitRow>> = [
    // 開閉列は 20px のボタン + 左右 16px の余白 (Figma Expand)
    { key: 'expand', header: '', width: '52px', cell: () => null },
    {
      key: 'name',
      ...sortableOf('name', f('name')),
      cell: unitCell((unit) => unit.unitName),
    },
    {
      key: 'areaCode',
      ...sortableOf('areaCode', f('areaCode')),
      cell: unitCell(areaCell),
    },
    {
      key: 'generatorCategory',
      header: f('generatorCategory'),
      cell: unitCell((unit) =>
        generatorCategoryLabel(t, unit.generatorCategory),
      ),
    },
    {
      key: 'authorizedOutputKw',
      ...sortableOf('authorizedOutputKw', f('authorizedOutputKw')),
      format: 'number',
      cell: unitCell((unit) => unit.authorizedOutputKw.toLocaleString('ja-JP')),
    },
    {
      key: 'receivingPointId',
      header: f('receivingPointId'),
      cell: unitCell((unit) => unit.receivingPointId || NO_VALUE),
    },
    {
      key: 'plantCode',
      header: f('plantCode'),
      cell: unitCell(plantCodeCell),
    },
    {
      key: 'affiliationBg',
      header: f('affiliationBg'),
      cell: unitCell(affiliationCell),
    },
    {
      key: 'operationStartDate',
      header: f('operationStartDate'),
      nowrap: true,
      cell: unitCell((unit) => dateCell(unit.operationStartDate)),
    },
    {
      key: 'operationEndDate',
      header: f('operationEndDate'),
      nowrap: true,
      cell: unitCell((unit) => dateCell(unit.operationEndDate)),
    },
    {
      key: 'updatedAt',
      header: t('common:audit.updatedAtHeader'),
      nowrap: true,
      cell: unitCell((unit) => (
        <AuditCell updatedAt={unit.updatedAt} updatedBy={undefined} />
      )),
    },
    {
      key: 'updatedBy',
      header: t('common:audit.updatedByHeader'),
      nowrap: true,
      cell: unitCell((unit) => resolveDisplayName(unit.updatedBy) ?? NO_VALUE),
    },
  ]

  const mixedCell = (
    mixed: MixedValue<AreaCode>,
  ): MasterDataTableGroupCell['content'] =>
    mixed.value !== undefined ? (
      convertAreaLabel(mixed.value)
    ) : (
      <Text
        as="span"
        display="block"
        minHeight="1em"
        title={t('page:adminPlantUnitMaster.unit.group.mixedHint', {
          values: mixed.values.map(convertAreaLabel).join(' / '),
        })}
      />
    )

  const groupRowCells = (
    row: GroupedUnitRow,
  ): MasterDataTableGroupCell[] | undefined => {
    if (row.kind !== 'plant') return undefined
    const { group } = row
    const siteAudit = siteAudits?.get(group.siteId)
    const toggleLabel = t(
      row.collapsed
        ? 'page:adminPlantUnitMaster.unit.group.expand'
        : 'page:adminPlantUnitMaster.unit.group.collapse',
      { name: group.siteName },
    )
    return [
      {
        key: 'expand',
        align: 'center',
        isExpandedTrigger: !row.collapsed,
        content: (
          <IconOnlyButton
            boxSize={iconSize.labeledButton}
            minWidth={iconSize.labeledButton}
            aria-expanded={!row.collapsed}
            label={toggleLabel}
            onClick={() => onToggleGroup(group.siteId)}
            size="sm"
            variant="ghost"
          >
            {row.collapsed ? (
              <FaChevronRightRegular boxSize={iconSize.labeledButton} />
            ) : (
              <FaChevronDownRegular boxSize={iconSize.labeledButton} />
            )}
          </IconOnlyButton>
        ),
      },
      {
        key: 'name',
        content: (
          <>
            <Text as="span">
              {group.siteName === '' ? NO_VALUE : group.siteName}
            </Text>
            <Text
              as="span"
              marginLeft={2}
              color="Text.midEmphasis"
              fontWeight="normal"
              title={t('page:adminPlantUnitMaster.unit.group.unitCountHint')}
            >
              {t('page:adminPlantUnitMaster.unit.group.unitCountShort', {
                count: group.units.length,
              })}
            </Text>
          </>
        ),
      },
      {
        key: 'updatedAt',
        content: (
          <AuditCell updatedAt={siteAudit?.updatedAt} updatedBy={undefined} />
        ),
      },
      {
        key: 'updatedBy',
        content: resolveDisplayName(siteAudit?.updatedBy) ?? NO_VALUE,
      },
      { key: 'areaCode', content: mixedCell(group.areaCodes) },
      {
        key: 'authorizedOutputKw',
        align: 'right',
        title: t('page:adminPlantUnitMaster.unit.group.totalOutputHint'),
        content: group.totalAuthorizedOutputKw.toLocaleString('ja-JP'),
      },
      {
        key: 'operationStartDate',
        title: t('page:adminPlantUnitMaster.unit.group.operationStartHint'),
        content: dateCell(group.earliestOperationStart),
      },
      {
        key: 'operationEndDate',
        title: t('page:adminPlantUnitMaster.unit.group.operationEndHint'),
        content: dateCell(group.latestOperationEndWhenAllRetired),
      },
    ]
  }

  return (
    <MasterDataTable
      actionColWidth="128px"
      columns={columns}
      emptyLabel={emptyLabel}
      errorLabel={errorLabel}
      getGroupRowCells={groupRowCells}
      getRowKey={(row) => row.key}
      groupRowVariant="expand"
      isLoading={isLoading}
      onRowClick={(row) => {
        if (row.kind === 'unit') onOpenDetail(row.unit)
      }}
      renderRowActions={(row) =>
        row.kind === 'unit' ? (
          <HStack gap={1}>
            <EditIconButton
              ariaLabel={t('page:adminPlantUnitMaster.unit.detailAction')}
              onClick={() => onOpenDetail(row.unit)}
            />
            <DeleteIconButton onClick={() => onDelete(row.unit)} />
          </HStack>
        ) : null
      }
      rows={rows}
    />
  )
}
